import * as Cl from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { dsClass } from '../../common/constants/dsClasses.js';
import { closeMsg, t } from '../../common/i18n/index.js';
import { CHARGER_IMAGE_URL } from './DeviceDetailsAccessoryPictureCoCo.js';

// TODO: Replace these hardcoded charging requirements with real device-specific values once PIM integration is available.
const CHARGING_REQUIREMENTS = { minimumPower: '5', maximumPower: '80' };

export const DeviceDetailsChargerCoCo = () => {
  const chargerRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<Cl.PopoverImperativeHandleRef>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const label = t.UT0C('Charger not included');
  const powerRange = `${CHARGING_REQUIREMENTS.minimumPower} - ${CHARGING_REQUIREMENTS.maximumPower}`;
  const requirementsLabel = t.ZYP7('Charging requirements: {} W, USB PD', powerRange);
  const popoverDescription = t.AX7R(
    'The device requires a charger with a power output of at least {} W. To achieve the maximum charging speed of the device, the charger must have a power output of at least {} W. The device supports USB PD fast charging.',
    CHARGING_REQUIREMENTS.minimumPower,
    CHARGING_REQUIREMENTS.maximumPower
  );

  return (
    <div className="of-device-details-charger-coco" ref={chargerRef}>
      {isHydrated ? (
        <Cl.Popover
          className="of-device-details-charger-coco__popover"
          componentRef={popoverRef}
          triggerElement={
            <button
              type="button"
              onKeyDown={event => {
                if (event.key === 'Escape' && event.currentTarget.getAttribute('aria-expanded') === 'true') {
                  event.preventDefault();
                  event.stopPropagation();
                  popoverRef.current?.closePopover();
                }
              }}
            >
              <Cl.Icon icon="information" size="s" />
            </button>
          }
          placement="right"
          i18n_popover_triggerAriaLabel={label}
          i18n_popover_closeButtonTitle={t.WOYD(closeMsg)}
          onClose={() => {
            // Avoid stale positioning when the panel is reopened after a layout change.
            const popover = chargerRef.current?.querySelector<HTMLElement>('[role="tooltip"]');
            popover?.style.removeProperty('left');
            popover?.style.removeProperty('top');
            chargerRef.current
              ?.querySelector<HTMLButtonElement>('button[aria-expanded]')
              ?.focus({ preventScroll: true });
          }}
        >
          <strong className="of-device-details-charger-coco__popover-title">{label}</strong>
          <span className="of-device-details-charger-coco__popover-description">{popoverDescription}</span>
        </Cl.Popover>
      ) : (
        <span className="ds-popover-holder">
          <button type="button" className="ds-popover-trigger" aria-label={label} aria-expanded={false} disabled>
            <Cl.Icon icon="information" size="s" />
          </button>
        </span>
      )}
      <div className="of-device-details-charger-coco__symbols">
        <div data-testid="device-charger-requirements-icon" className="of-device-details-charger-coco__requirements">
          <span className={dsClass.SR_ONLY}>{requirementsLabel}</span>
          <svg viewBox="0 0 66 96" fill="none" preserveAspectRatio="xMidYMid meet" aria-hidden="true">
            <title>{requirementsLabel}</title>
            <path
              d="M1 33h64v62H1zM12 17h42v16H12zM20 1h4v16h-4zM42 1h4v16h-4z"
              stroke="black"
              strokeWidth="1.75"
              strokeLinejoin="round"
            />
            <text data-testid="device-charger-requirements-power" x="50%" y="50%">
              {powerRange}
            </text>
            <text data-testid="device-charger-requirements-unit" x="50%" y="70%">
              W
            </text>
            <text data-testid="device-charger-requirements-usbpd" x="50%" y="90%">
              USB PD
            </text>
          </svg>
        </div>
        <div className="of-device-details-charger-coco__symbol">
          <img
            className="of-device-details-charger-coco__icon"
            src={CHARGER_IMAGE_URL}
            alt={label}
            width="50"
            height="72"
          />
        </div>
      </div>
    </div>
  );
};
